import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPencil } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from 'store';
import MagicButton from 'components/MagicButton/MagicButton';
import {
  useGetMyAffiliateQuery,
  useEnrollAffiliateMutation,
  useUpdateMyAffiliateMutation,
} from 'store/services/affiliates/affiliatesApi';
import { getReferralUrl } from 'utils/referralLink';
import { AssetUrls } from 'config/assets';
import AffiliateTermsContent, {
  AFFILIATE_TERMS_VERSION,
} from 'screens/Legal/AffiliateTermsContent';
import InfoTooltip from 'components/InfoTooltip/InfoTooltip';
import EarningsHistoryTable from './EarningsHistoryTable';
import './AffiliatesTab.css';
import { copyToClipboard } from 'utils/copyToClipboard';

// Oracle's Insight art — the shared Elite marketing asset. Matches
// EliteUpsellBanner/EliteUpsellPrompt (kept in sync across upsell surfaces).
const BNG_47_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value.trim());

const formatCents = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const isConflictError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  return (error as FetchBaseQueryError).status === 409;
};

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="affil-modal-overlay" onClick={onClose}>
      <div
        className="affil-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Partner Program Terms & Conditions"
      >
        <div className="affil-modal-header">
          <h2 className="affil-modal-title">Partner Program Terms &amp; Conditions</h2>
          <button
            className="affil-modal-close"
            onClick={onClose}
            aria-label="Close terms and conditions"
            type="button"
          >
            &times;
          </button>
        </div>
        <div className="affil-modal-body">
          <AffiliateTermsContent />
        </div>
        <div className="affil-modal-footer">
          <MagicButton label="Close" color="dark-grey" onPress={onClose} />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const AffiliatesTab: React.FC = () => {
  const username = useSelector((state: RootState) => state.auth.user)?.username;

  const { data, isLoading, isError } = useGetMyAffiliateQuery();
  const [enrollAffiliate, { isLoading: isEnrolling }] = useEnrollAffiliateMutation();
  const [updateMyAffiliate, { isLoading: isUpdating }] = useUpdateMyAffiliateMutation();

  // Enroll form state
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollEmail, setEnrollEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // PayPal email edit state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editEmail, setEditEmail] = useState('');

  const handleEnroll = async () => {
    try {
      await enrollAffiliate({
        paypalEmail: enrollEmail.trim(),
        termsVersion: AFFILIATE_TERMS_VERSION,
      }).unwrap();
      toast.success('Welcome to the Partner Program!');
    } catch (error) {
      if (isConflictError(error)) {
        toast.error("You're already enrolled.");
      } else {
        toast.error('Failed to enroll. Please try again.');
      }
    }
  };

  const handleCopyReferralLink = async () => {
    if (!username) return;
    try {
      if (await copyToClipboard(getReferralUrl(username))) toast.success('Copied!');
      else toast.error('Failed to copy link');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleStartEditEmail = (currentEmail: string) => {
    setEditEmail(currentEmail);
    setIsEditingEmail(true);
  };

  const handleSaveEmail = async () => {
    if (!isValidEmail(editEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    try {
      await updateMyAffiliate({ paypalEmail: editEmail.trim() }).unwrap();
      toast.success('PayPal email updated.');
      setIsEditingEmail(false);
    } catch {
      toast.error('Failed to update PayPal email. Please try again.');
    }
  };

  if (isLoading) {
    return <div className="affil-loading">Loading partner information...</div>;
  }

  if (isError || !data) {
    return (
      <div className="affil-error">
        Failed to load partner information. Please refresh the page.
      </div>
    );
  }

  // ── Unenrolled state ────────────────────────────────────────────────────
  if (!data.enrolled) {
    const canSubmitEnroll = isValidEmail(enrollEmail) && termsAccepted && !isEnrolling;

    return (
      <div className="affil-tab">
        <div className="affil-card affil-card--promo">
          {/* Header band — Oracle's Insight art (the shared Elite marketing
              asset, kept in sync with EliteUpsellBanner/Prompt) with the
              upsell surfaces' darkening overlay and the Elite plaque. */}
          <div
            className="affil-hero"
            style={{ backgroundImage: `url(${AssetUrls.cards.getCardImage(BNG_47_SCRYFALL_ID, 'art_crop')})` }}
          >
            <div className="affil-hero__overlay" />
            <img
              src={AssetUrls.icons.elite.badge}
              alt="Elite"
              className="affil-hero__badge"
            />
            <div className="affil-hero__text">
              <h3 className="affil-hero__headline">Earn $5 cash</h3>
              <p className="affil-hero__subline">for each Elite subscriber you refer</p>
            </div>
          </div>

          <div className="affil-card-body">
            <ul className="affil-bullets">
              <li>Share your personal referral link with friends and followers.</li>
              <li>Get credit when someone subscribes within 30 days of clicking your link.</li>
              <li>Earn $5 for every verified conversion.</li>
              <li>Get paid monthly via PayPal.</li>
            </ul>

          {!showEnrollForm ? (
            <div className="affil-cta-row">
              <button
                type="button"
                className="affil-pill-cta"
                onClick={() => setShowEnrollForm(true)}
              >
                Become a Partner
              </button>
            </div>
          ) : (
            <div className="affil-enroll-form">
              <label className="affil-field-label" htmlFor="affil-paypal-email">
                PayPal Email
                <InfoTooltip text="This is the account we'll make payment into." />
              </label>
              <input
                id="affil-paypal-email"
                className="affil-input"
                type="email"
                value={enrollEmail}
                onChange={(e) => setEnrollEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              {enrollEmail.length > 0 && !isValidEmail(enrollEmail) && (
                <p className="affil-field-error">Please enter a valid email address.</p>
              )}

              <label className="affil-terms-row">
                <input
                  type="checkbox"
                  className="affil-checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <button
                    type="button"
                    className="affil-terms-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                  >
                    Partner Program Terms &amp; Conditions
                  </button>
                </span>
              </label>

              <div className="affil-cta-row">
                <MagicButton
                  label={isEnrolling ? 'Enrolling...' : 'Enroll'}
                  color="purple"
                  onPress={handleEnroll}
                  isDisabled={!canSubmitEnroll}
                  isLoading={isEnrolling}
                />
              </div>
            </div>
          )}
          </div>
        </div>

        <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      </div>
    );
  }

  // ── Enrolled state ──────────────────────────────────────────────────────
  const affiliate = data.affiliate;
  const stats = data.stats;
  const referralUrl = username ? getReferralUrl(username) : '';

  return (
    <div className="affil-tab">
      {affiliate && affiliate.status !== 'active' && (
        <p className="affil-status-notice">
          Your partner account is currently{' '}
          {affiliate.status === 'paused' ? 'paused' : 'suspended'}. New conversions are not
          being credited.
        </p>
      )}

      <div className="affil-card">
        <div className="affil-section-title-row">
          <h3 className="affil-section-title">Your Referral Link</h3>
          <Link to="/affiliates/dashboard" className="affil-dashboard-link">
            View Dashboard →
          </Link>
        </div>
        <div className="affil-referral-row">
          <input
            className="affil-input affil-referral-input"
            type="text"
            value={referralUrl}
            readOnly
            aria-label="Your referral link"
          />
          <button
            className="affil-icon-button"
            onClick={handleCopyReferralLink}
            title="Copy referral link"
            aria-label="Copy referral link"
            type="button"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </div>

        <div className="affil-paypal-row">
          <span className="affil-paypal-label">PayPal Email</span>
          {!isEditingEmail ? (
            <div className="affil-paypal-value">
              <span className="affil-paypal-email">{affiliate?.paypalEmail}</span>
              <button
                className="affil-icon-button"
                onClick={() => handleStartEditEmail(affiliate?.paypalEmail || '')}
                title="Edit PayPal email"
                aria-label="Edit PayPal email"
                type="button"
              >
                <FontAwesomeIcon icon={faPencil} />
              </button>
            </div>
          ) : (
            <div className="affil-paypal-edit">
              <input
                className="affil-input"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <div className="affil-paypal-edit-actions">
                <button
                  className="affil-small-button affil-small-button-primary"
                  onClick={handleSaveEmail}
                  disabled={isUpdating || !isValidEmail(editEmail)}
                  type="button"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="affil-small-button"
                  onClick={() => setIsEditingEmail(false)}
                  disabled={isUpdating}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {stats && (
        <div className="affil-tiles">
          <div className="affil-tile">
            <span className="affil-tile-label">Current Month Earnings</span>
            <span className="affil-tile-value">
              {formatCents(stats.currentMonthEarningsCents)}
            </span>
          </div>
          <div className="affil-tile">
            <span className="affil-tile-label">All-Time Earnings</span>
            <span className="affil-tile-value">{formatCents(stats.allTimeEarningsCents)}</span>
          </div>
          <div className="affil-tile">
            <span className="affil-tile-label">Total Conversions</span>
            <span className="affil-tile-value">{stats.totalConversions}</span>
          </div>
          <div className="affil-tile">
            <span className="affil-tile-label">Last Payout</span>
            {stats.lastPayout ? (
              <>
                <span className="affil-tile-value">
                  {formatCents(stats.lastPayout.amountCents)}
                </span>
                <span className="affil-tile-sub">{formatDate(stats.lastPayout.paidAt)}</span>
              </>
            ) : (
              <span className="affil-tile-value affil-tile-value-muted">No payouts yet</span>
            )}
          </div>
        </div>
      )}

      <EarningsHistoryTable />

      <div className="affil-footer">
        <Link to="/legal/affiliate-terms" className="affil-footer-link">
          View Full Terms &amp; Conditions
        </Link>
      </div>
    </div>
  );
};

export default AffiliatesTab;
